import { useField } from 'formik';
import { createPortal } from 'react-dom';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  InputLabel,
  Typography,
  FormHelperText,
  CircularProgress,
} from '@mui/material';
import {
  useSensor,
  DndContext,
  useSensors,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  DragEndEvent,
  closestCenter,
  DragStartEvent,
} from '@dnd-kit/core';

import { controlError, appendInputIdToLabel } from 'utils';
import { defaultLanguage, useTranslationContext } from 'context';
import { OutcomesFieldValue, OutcomesFieldSubmissionValue } from 'types';

import { OutcomesFieldProps } from '../types';

import { OutcomeCard, SortableOutcomeItem } from './card';
import { moveOutcome, buildOrderedOutcomes } from './config';

export const OutcomesField = ({
  id,
  label,
  inputId,
  isLoading,
  translations,
  isUnavailable,
  unavailableMessage,
}: OutcomesFieldProps) => {
  const { language } = useTranslationContext();
  const { t } = useTranslation(['common']);
  const [field, meta, helpers] = useField<OutcomesFieldSubmissionValue>(id);
  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));
  const [activeOutcomeId, setActiveOutcomeId] = useState<string | null>(null);

  const translatedLabel = useMemo(() => {
    if (!language || !translations || language === defaultLanguage) {
      return appendInputIdToLabel(label, inputId);
    }

    return appendInputIdToLabel(
      label ? translations[language]?.label || label : label,
      inputId
    );
  }, [label, language, translations, inputId]);

  const value = useMemo<OutcomesFieldValue>(() => {
    const outcomes = field.value?.outcomes ?? [];
    const orderedIds = field.value?.orderedIds ?? [];
    const orderedOutcomes = buildOrderedOutcomes(outcomes, orderedIds);

    return {
      outcomes: orderedOutcomes,
      orderedIds: orderedOutcomes.map((item) => item.id),
    };
  }, [field.value?.orderedIds, field.value?.outcomes]);

  const errorText = controlError(meta, id, translatedLabel);

  const handleDragEnd = ({ over, active }: DragEndEvent) => {
    setActiveOutcomeId(null);

    if (!over || active.id === over.id) {
      return;
    }

    const nextOutcomes = moveOutcome(
      value.outcomes,
      String(active.id),
      String(over.id)
    );

    helpers.setValue(
      {
        outcomes: nextOutcomes,
        orderedIds: nextOutcomes.map((item) => item.id),
      },
      true
    );
  };

  const handleDragStart = ({ active }: DragStartEvent) => {
    setActiveOutcomeId(String(active.id));
  };

  const activeOutcomeIndex = value.outcomes.findIndex(
    (outcome) => outcome.id === activeOutcomeId
  );
  const activeOutcome =
    activeOutcomeIndex >= 0 ? value.outcomes[activeOutcomeIndex] : undefined;

  return (
    <Box>
      {translatedLabel && (
        <InputLabel className="mb-2">{translatedLabel}</InputLabel>
      )}

      <DndContext
        sensors={sensors}
        onDragEnd={handleDragEnd}
        onDragStart={handleDragStart}
        collisionDetection={closestCenter}
      >
        <Box className="flex flex-col gap-3">
          {value.outcomes.map((outcome, index) => (
            <SortableOutcomeItem
              index={index}
              key={outcome.id}
              outcome={outcome}
            />
          ))}

          {!value.outcomes.length && (
            <>
              {isLoading && <CircularProgress size={24} />}
              {!isLoading && !isUnavailable && (
                <Typography variant="body2" className="text-gray-neutral">
                  {t('noOutcomesAvailable')}
                </Typography>
              )}
              {!isLoading && isUnavailable && unavailableMessage && (
                <FormHelperText error>{unavailableMessage}</FormHelperText>
              )}
            </>
          )}
        </Box>
        {createPortal(
          <DragOverlay>
            {activeOutcome && activeOutcomeIndex >= 0 && (
              <OutcomeCard
                isDraggingOverlay
                index={activeOutcomeIndex}
                label={activeOutcome.label}
                description={activeOutcome.description}
              />
            )}
          </DragOverlay>,
          document.body
        )}
      </DndContext>

      {errorText && <FormHelperText error>{errorText}</FormHelperText>}
    </Box>
  );
};
